// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useOverlayBadge from "@insite/client-framework/Common/Hooks/useOverlayBadge";
import useWidgetBadges from "@insite/client-framework/Common/Hooks/useWidgetBadges";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { ProductContext } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import updateProductInfo from "@insite/client-framework/Store/Components/ProductInfoList/Handlers/UpdateProductInfo";
import { getProductInfoFromList } from "@insite/client-framework/Store/Components/ProductInfoList/ProductInfoListSelectors";
import sendProductRecommendationTracking from "@insite/client-framework/Store/Components/ProductRecommendations/Handlers/SendProductRecommendationTracking";
import translate from "@insite/client-framework/Translate";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";
import ProductAddToCartButton from "@insite/content-library/Components/ProductAddToCartButton";
import ProductAddToListLink, {
    ProductAddToListLinkStyles,
} from "@insite/content-library/Components/ProductAddToListLink";
import { ProductAvailabilityStyles } from "@insite/content-library/Components/ProductAvailability";
import ProductBrand, { ProductBrandStyles } from "@insite/content-library/Components/ProductBrand";
import ProductContextAvailability from "@insite/content-library/Components/ProductContextAvailability";
import ProductDescription, { ProductDescriptionStyles } from "@insite/content-library/Components/ProductDescription";
import ProductImage, { ProductImageStyles } from "@insite/content-library/Components/ProductImage";
import ProductImageBadges from "@insite/content-library/Components/ProductImageBadges";
import ProductPartNumbers, { ProductPartNumbersStyles } from "@insite/content-library/Components/ProductPartNumbers";
import ProductPrice, { ProductPriceStyles } from "@insite/content-library/Components/ProductPrice";
import ProductQuantityBreakPricing, {
    ProductQuantityBreakPricingStyles,
} from "@insite/content-library/Components/ProductQuantityBreakPricing";
import ProductQuantityOrdered from "@insite/content-library/Components/ProductQuantityOrdered";
import ProductTextBadges from "@insite/content-library/Components/ProductTextBadges";
import Badge from "@insite/mobius/Badge";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import { GridItemProps } from "@insite/mobius/GridItem";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import ProductGridCarouselContainer, {
    ProductGridCarouselContainerStyle,
} from "@steiner/App/Components/Product/ProductGridCarouselContainer";
import { QtyFieldStyle } from "@steiner/App/Components/Qty/QtyField";
import React, { useState } from "react";
import { connect, ResolveThunks } from "react-redux";

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => ({
    productInfo: getProductInfoFromList(state, ownProps.carouselId, ownProps.product.id),
});

const mapDispatchToProps = {
    updateProductInfo,
    sendProductRecommendationTracking,
};

interface OwnProps {
    carouselId: string;
    product: ProductModel;
    recommendationId?: number;
    showImage: boolean;
    showBrand: boolean;
    showTitle: boolean;
    showPartNumbers: boolean;
    showPrice: boolean;
    showAddToCart: boolean;
    showAddToList: boolean;
    showImageBadges?: boolean;
    maximumNumberOfImageBadges?: number;
    showTextBadges?: boolean;
    maximumNumberOfTextBadges?: number;
    extendedStyles?: ProductCarouselProductItemStyles;
    showAvailability?: boolean;
    actionsTemplate?: (product: ProductModel) => React.ReactNode;
}

type Props = OwnProps & ResolveThunks<typeof mapDispatchToProps> & ReturnType<typeof mapStateToProps>;

export interface ProductCarouselProductItemStyles {
    productImageStyles?: ProductImageStyles;
    productBrandWrapper?: InjectableCss;
    productBrandStyles?: ProductBrandStyles;
    productDescriptionStyles?: ProductDescriptionStyles;
    productPartNumbersStyles?: ProductPartNumbersStyles;
    productPriceWrapper?: InjectableCss;
    productQuantityBreakPricingStyles?: ProductQuantityBreakPricingStyles;
    addToCartButtonGridItem?: GridItemProps;
    productAddToCartButtonStyles?: ButtonPresentationProps;
    productAddToListLinkStyles?: ProductAddToListLinkStyles;
    badgeAreaWrapper?: InjectableCss;
    badgeImageWrapper?: InjectableCss;
    badgeText?: TypographyPresentationProps;
    badgeImageProps?: JSX.IntrinsicElements["img"];
    extendedProductGridCarouselContainerStyles?: ProductGridCarouselContainerStyle;
    overlayWrapper?: InjectableCss;
    productPrice?: ProductPriceStyles;
    productAvailabilityStyles?: ProductAvailabilityStyles;
    addToListWrapper?: InjectableCss;
    quantityOrderedStyles?: QtyFieldStyle;
}

export const productCarouselProductItemStyles: ProductCarouselProductItemStyles = {};

const ProductCarouselProductItem = ({
    carouselId,
    product,
    showImage,
    showBrand,
    showTitle,
    showPartNumbers,
    showPrice,
    showAddToCart,
    showAddToList,
    showImageBadges,
    maximumNumberOfImageBadges,
    showTextBadges,
    maximumNumberOfTextBadges,
    updateProductInfo,
    extendedStyles,
    productInfo,
    recommendationId,
    sendProductRecommendationTracking,
    showAvailability,
    actionsTemplate,
}: Props) => {
    const [styles] = useState(() => mergeToNew(productCarouselProductItemStyles, extendedStyles));

    const updateQuantity = (qtyOrdered: number) => {
        updateProductInfo({ id: carouselId, productId: product.id, qtyOrdered });
    };

    const updateUnitOfMeasure = (unitOfMeasure: string) => {
        updateProductInfo({ id: carouselId, productId: product.id, unitOfMeasure });
    };

    if (!product || !productInfo) {
        return null;
    }

    const productContext = {
        product,
        productInfo,
        onUnitOfMeasureChanged: updateUnitOfMeasure,
        onQtyOrderedChanged: updateQuantity,
    };

    const trackProductRecommendation = (isNewPageWasOpen: boolean) => {
        if (recommendationId) {
            sendProductRecommendationTracking({ recommendationId, isNewPageWasOpen });
        }
    };

    const { showOverlayBadge, overlayBadge, badgeAreaWrapperRef, overlayBadgeWrapperClickHandler } = useOverlayBadge({
        productContext,
        showTextBadge: showTextBadges,
        showImageBadge: showImageBadges,
        styles,
    });

    const { imageBadges, textBadges } = useWidgetBadges({ productContext, showTextBadges, showImageBadges });

    return (
        <ProductContext.Provider value={productContext}>
            <ProductGridCarouselContainer extendedStyles={styles.extendedProductGridCarouselContainerStyles}>
                {showImage && !showOverlayBadge && (
                    <ProductGridCarouselContainer.Image>
                        <ProductImage
                            product={productContext}
                            onClick={() => trackProductRecommendation(true)}
                            extendedStyles={styles.productImageStyles}
                            {...{ showPreviouslyPurchasedTag: true }}
                        />
                    </ProductGridCarouselContainer.Image>
                )}
                {showImage && showOverlayBadge && overlayBadge && (
                    <ProductGridCarouselContainer.Image>
                        <StyledWrapper
                            {...styles.badgeAreaWrapper}
                            ref={badgeAreaWrapperRef}
                            data-test-selector="productCarouselBadge"
                        >
                            <ProductImage extendedStyles={styles.productImageStyles} product={productContext} />
                            <StyledWrapper {...styles.overlayWrapper} onClick={overlayBadgeWrapperClickHandler}>
                                <Badge
                                    type={overlayBadge.badgeType}
                                    value={
                                        overlayBadge.badgeType === "Image"
                                            ? overlayBadge.largeImageBadgePath
                                            : overlayBadge.displayText
                                    }
                                    badgeTextColor={overlayBadge.textColorHexCode}
                                    badgeBackgroundColor={overlayBadge.badgeColorHexCode}
                                    badgeTextRounding={overlayBadge.badgeStyle === "Round" ? 20 : 0}
                                    imageAltText={
                                        overlayBadge.imageAltText ? overlayBadge.imageAltText : overlayBadge.name
                                    }
                                    textBadgeCss={styles.badgeText}
                                    imageBadgeProps={styles.badgeImageProps}
                                />
                            </StyledWrapper>
                        </StyledWrapper>
                    </ProductGridCarouselContainer.Image>
                )}
                <ProductGridCarouselContainer.Info>
                    {showBrand && product.brand && (
                        <StyledWrapper {...styles.productBrandWrapper}>
                            <ProductBrand brand={product.brand} extendedStyles={styles.productBrandStyles} />
                        </StyledWrapper>
                    )}
                    {showTitle && (
                        <ProductDescription
                            product={productContext}
                            onClick={() => trackProductRecommendation(true)}
                            extendedStyles={styles.productDescriptionStyles}
                        />
                    )}
                    {showPartNumbers && (
                        <ProductPartNumbers
                            productNumber={product.productNumber}
                            showProductNumber={!product.customerProductNumber}
                            customerProductNumber={product.customerProductNumber}
                            manufacturerItem={product.manufacturerItem}
                            showManufacturerItem
                            extendedStyles={styles.productPartNumbersStyles}
                        />
                    )}
                    {showTextBadges && textBadges && (
                        <ProductTextBadges badgeList={textBadges} maxBadges={maximumNumberOfTextBadges ?? 4} />
                    )}
                    {showImageBadges && imageBadges && (
                        <ProductImageBadges badgeList={imageBadges} maxBadges={maximumNumberOfImageBadges ?? 4} />
                    )}
                </ProductGridCarouselContainer.Info>
                <ProductGridCarouselContainer.Price>
                    {showPrice && (
                        <StyledWrapper {...styles.productPriceWrapper}>
                            <VisuallyHidden as="label" htmlFor={`${carouselId}-uom`} id={`${carouselId}-uom-label`}>
                                {translate("U/M")}
                            </VisuallyHidden>
                            <ProductPrice
                                product={productContext}
                                showLabel={false}
                                showSalePriceLabel={false}
                                extendedStyles={styles.productPrice}
                            />
                            <ProductQuantityBreakPricing extendedStyles={styles.productQuantityBreakPricingStyles} />
                        </StyledWrapper>
                    )}
                </ProductGridCarouselContainer.Price>
                <ProductGridCarouselContainer.Availability>
                    {showAvailability && (
                        <ProductContextAvailability extendedStyles={styles.productAvailabilityStyles} />
                    )}
                </ProductGridCarouselContainer.Availability>
                <ProductGridCarouselContainer.AddToCartActions>
                    {showAddToCart && (
                        <>
                            <ProductQuantityOrdered
                                id={`${carouselId}-qty`}
                                extendedStyles={styles.quantityOrderedStyles}
                            />
                            <ProductAddToCartButton extendedStyles={styles.productAddToCartButtonStyles} />
                        </>
                    )}
                </ProductGridCarouselContainer.AddToCartActions>
                <ProductGridCarouselContainer.AddToList>
                    {actionsTemplate ? actionsTemplate(product) : []}
                    {showAddToList && (
                        <StyledWrapper {...styles.addToListWrapper}>
                            <ProductAddToListLink
                                data-test-selector="addToListLink"
                                extendedStyles={styles.productAddToListLinkStyles}
                                labelOverride={translate("Product Group")}
                            />
                        </StyledWrapper>
                    )}
                </ProductGridCarouselContainer.AddToList>
            </ProductGridCarouselContainer>
        </ProductContext.Provider>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(ProductCarouselProductItem);
